import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CellComponent, ColumnDefinition } from "tabulator-tables";
import {
  SHARED_OBJECT_TYPE_SERVER_TO_UI,
  SHARED_OBJECT_TYPE_UI_TO_SERVER,
} from "../../api/compute/_CommonPublicationsSubscriptions";
import { CombinedProducts } from "../../api/compute/CombinedProducts";
import { CombinedStrategies } from "../../api/compute/CombinedStrategies";
import { Publications } from "../../api/compute/Publications";
import { Strategies } from "../../api/compute/Strategies";
import { Structures } from "../../api/compute/Structures";
import Tabulator, { EventsType } from "../../components/Tabulator";
import { deepClone } from "../../deepClone";
import { useAuth } from "../../hooks/useAuth";
import { useFeedbackSystem } from "../../hooks/useFeedbackSystem";
import { mapEntity } from "./TagsUtils";
import Editor from "./widgets/Editor";

type TagManagerTabsType = "strategy" | "screening";
export default function Tags() {
  const auth = useAuth();
  const [tags, setTags] = useState<any>(null);
  const [objects, setobjects] = useState<any>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<any>(null);
  const feedback = useFeedbackSystem();
  const strategyAPI = useMemo(() => new Strategies(auth.user35), [auth.user35]);

  const itemButtons = useMemo((): {
    title: string;
    value: TagManagerTabsType;
  }[] => {
    return [
      {
        title: "Strategies",
        value: "strategy",
      },
      {
        title: "Screenings",
        value: "screening",
      },
    ];
  }, []);

  const [tab, setTab] = useState<TagManagerTabsType>("screening");

  const mapTags = useMemo(() => {
    /**
     * mapTags è una lookup table creata per velocizzare e semplificare
     * l’accesso ai dati dei tag. La struttura originale di `tags` è
     * annidata: ogni gruppo contiene una lista di filtri. Per capire
     * rapidamente cosa rappresenta un certo id (sia esso un gruppo come
     * "group-sector" o un filtro come "EU", "trend", "45", ecc.) sarebbe
     * necessario ciclare ogni volta dentro `tags` e poi nei suoi
     * `filters`.
     *
     * Per evitare ricerche lente e ripetitive, mapTags trasforma l’intero
     * array in un dizionario “piatto”:
     *
     *   - mapTags[idDelGruppo] → oggetto gruppo
     *   - mapTags[idDelFiltro] → oggetto filtro
     *
     * In questo modo qualsiasi id può essere recuperato in O(1),
     * permettendo:
     *   - un rendering più efficiente della tabella
     *   - una risoluzione istantanea dei valori dei tag
     *   - un accesso uniforme sia ai gruppi che ai singoli filtri
     *
     * Viene memoizzato con useMemo per rigenerarlo solo quando `tags`
     * cambia, evitando lavoro inutile a ogni re-render.
     */
    var mapTags = {};
    if (tags != null) {
      for (let i = 0; i < tags.length; i++) {
        var item = tags[i];
        mapTags[item.id] = item;
        for (let j = 0; j < item.filters.length; j++) {
          var filter = item.filters[j];
          mapTags[filter.id] = filter;
        }
      }
    }
    console.log("mapTags", mapTags);
    return mapTags;
  }, [tags]);

  const strategiesGet = useCallback(async () => {
    try {
      const publicationData = await new Publications().get();

      // fetch all the details for the strategies, combined strategies and combined products.
      let strategies = await strategyAPI.fetch(
        publicationData.strategies.map((item) => item.id),
        ["name", "tags", "type", "object.entity_type"],
      );
      strategies = strategies.data[0].rows;
      let combinedStrategies = await new CombinedStrategies().fetch(
        publicationData.combinedStrategies.map((item) => item.id),
        ["name", "tags", "type"],
        "COMBINED_STRATEGY",
      );
      combinedStrategies = combinedStrategies.data[0].rows;
      let combinedProducts = await new CombinedProducts().fetch(
        publicationData.combinedProducts.map((item) => item.id),
        ["name", "tags", "type"],
        "COMBINED_PRODUCT",
      );
      combinedProducts = combinedProducts.data[0].rows;

      const _filter = (property, value) => {
        return function (item) {
          if (item[property] === value) {
            return true;
          }
          return false;
        };
      };

      // filter only builder strategies(created with the Strategy Builder)
      strategies = strategies.filter(_filter("entity_type", "BUILDER"));

      var publication: any = null;
      var publications = publicationData;
      var publicationsMap = {};
      for (var type in publications) {
        publicationsMap[type] = {};
        for (let i = 0; i < publications[type].length; i++) {
          publication = publications[type][i];
          publicationsMap[type][publication.id] = publication;
        }
      }
      strategies = strategies.map((item) =>
        mapEntity(
          item,
          "strategies",
          SHARED_OBJECT_TYPE_SERVER_TO_UI,
          publicationsMap["strategies"],
        ),
      );
      combinedStrategies = combinedStrategies.map((item) =>
        mapEntity(
          item,
          "combinedStrategies",
          SHARED_OBJECT_TYPE_SERVER_TO_UI,
          publicationsMap["combinedStrategies"],
        ),
      );
      combinedProducts = combinedProducts.map((item) =>
        mapEntity(
          item,
          "combinedProducts",
          SHARED_OBJECT_TYPE_SERVER_TO_UI,
          publicationsMap["combinedProducts"],
        ),
      );
      return [...strategies, ...combinedStrategies, ...combinedProducts];
    } catch (error) {
      throw new Error();
    }
  }, [strategyAPI]);

  const screeningGet = useCallback(async () => {
    try {
      // 1. Carico le pubblicazioni
      const publicationData = await new Publications().get();

      // 2. Fetch degli screening (come CombinedProducts)
      let screenings = await new CombinedProducts().fetch(
        publicationData.screening.map((item) => item.id),
        ["name", "tags", "type"],
      );
      screenings = screenings.data[0].rows;

      // 3. Tipo server → tipo UI (conversione standard)
      const objectTypes = SHARED_OBJECT_TYPE_SERVER_TO_UI;

      // 4. Costruisco publicationsMap come in strategiesGet()
      const publicationsMap = {};
      for (const type in publicationData) {
        publicationsMap[type] = {};
        for (let i = 0; i < publicationData[type].length; i++) {
          const pub = publicationData[type][i];
          publicationsMap[type][pub.id] = pub;
        }
      }

      // 5. Normalizzo gli screenings tramite mapEntity()
      const normalizedScreenings = screenings.map((item) =>
        mapEntity(item, "screening", objectTypes, publicationsMap["screening"]),
      );

      // 6. Ritorno dati pronti per tabella
      return normalizedScreenings;
    } catch (error) {
      console.error("Error fetching screenings:", error);
      throw new Error("Failed to load screenings");
    }
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const fetchStrategies = useCallback(() => {
    if (auth.isLogged) {
      setIsLoading(true);
      strategiesGet().then(
        (res) => {
          console.log("fetched strategies/combined ", res);
          setobjects(res);
          setIsLoading(false);
        },
        (error) => {
          alert(error);
          setIsLoading(false);
          return error;
        },
      );
    }
  }, [auth.isLogged, strategiesGet]);

  const fetchScreenings = useCallback(() => {
    setIsLoading(true);
    screeningGet().then(
      (res) => {
        console.log("fetched screenings ", res);
        setobjects(res);
        setIsLoading(false);
      },
      (error) => {
        alert(error);
        setIsLoading(false);
        return error;
      },
    );
  }, [screeningGet]);

  const getTags = useCallback(async () => {
    const structures = new Structures();
    console.log("fetching tags from structures...");
    const _tags = await structures.tags();
    return _tags;
  }, []);

  const renderTag = useCallback(
    (parentId, datum) => {
      /**
       * renderTag è una funzione che si occupa di tradurre i tag grezzi
       * associati a un oggetto (strategy, combined product, ecc.) in una
       * stringa leggibile da visualizzare nella tabella.
       *
       * Ogni oggetto ha un campo `tags` che contiene valori come:
       *
       *   - per gruppi "single":    "EU", "WWW", "45", ...
       *   - per gruppi "multiple":  ["trend", "value", "growth"], ...
       *
       * Il problema è che questi valori sono solo ID. Per mostrarli come testo
       * (es. "Europe", "Trend", "Growth") bisogna risalire alle informazioni
       * del relativo filtro o gruppo. Senza una funzione centralizzata servirebbe
       * cercare manualmente ogni ID dentro l’intera struttura `tags`.
       *
       * renderTag risolve questo problema usando `mapTags` come lookup table:
       *
       *   - individua il gruppo (ad es. "group-market")
       *   - recupera i filtri validi del gruppo
       *   - estrae i valori selezionati dall’oggetto corrente
       *   - converte gli ID in nome leggibile tramite mapTags[id].name
       *   - compone la stringa finale da mostrare in tabella
       *
       * Inoltre gestisce correttamente le differenze tra:
       *   - gruppi “single” → un solo valore
       *   - gruppi “multiple” → lista di valori concatenati
       */
      var parent: any = mapTags[parentId];
      var filters: any = [];
      for (let i = 0; i < parent.filters.length; i++) {
        filters.push(parent.filters[i].id);
      }
      var tagName = "N/A";
      var found = false;

      if (parent != null) {
        tagName = parent.allName;
        if (
          datum != null &&
          datum.tags != null &&
          datum.tags.type === "list" &&
          datum.tags.data != null
        ) {
          var tags = datum.tags.data;
          for (let i = 0; i < tags.length; i++) {
            var tag = mapTags[tags[i]];
            if (tag != null && filters.indexOf(tag.id) !== -1) {
              if (parent.type === "single") {
                if (found) {
                  tagName = "(error)";
                  break;
                } else {
                  found = true;
                  tagName = tag.name;
                }
              } else {
                if (found) {
                  tagName = `${tagName}, ${tag.name}`;
                } else {
                  found = true;
                  tagName = tag.name;
                }
              }
            }
          }
        }
        if (
          datum != null &&
          datum.tags != null &&
          datum.tags.type === "object" &&
          datum.tags.data != null
        ) {
          tags = datum.tags.data;
          tag = tags[parentId];
          // if (tag != null && filters.indexOf(tag) !== -1) {
          if (parent.type === "single") {
            if (found) {
              tagName = "(error)";
            } else {
              found = true;
              tagName = mapTags[tag]?.name ?? tagName;
            }
          } else {
            if (found) {
              tagName = `${tagName}, ${tag.name}`;
            } else {
              found = true;
              tagName =
                tag?.map((item) => mapTags[item].name)?.join(", ") ?? "";
            }
          }
          // }
        }
        return tagName;
      }
    },
    [mapTags],
  );

  const tagFormatter = useCallback(
    (cell: CellComponent) => {
      return renderTag(cell.getField(), cell.getRow().getData());
    },
    [renderTag],
  );
  const _columns = useMemo(() => {
    const _c: ColumnDefinition[] = [
      {
        field: "UID",
        title: "ID",
        sorter: "number",
        formatter: (cell) => {
          const value = cell.getRow().getData().id;
          return `<strong>${value}</strong>`;
        },
      },
      {
        field: "type",
        title: "Type",
        sorter: "string",
        formatter: (cell: CellComponent) => {
          let temp = "";
          const cellValue = cell.getValue();
          switch (cellValue) {
            case SHARED_OBJECT_TYPE_UI_TO_SERVER.strategy:
              temp = "Strategy";
              break;
            case SHARED_OBJECT_TYPE_UI_TO_SERVER.combinedProduct:
              temp = "Combined SMS";
              break;
            case SHARED_OBJECT_TYPE_UI_TO_SERVER.combinedStrategy:
              temp = "Combined strategy";
              break;
            case SHARED_OBJECT_TYPE_UI_TO_SERVER.screening:
              temp = "Screening";
              break;
            default:
              temp = "Error!";
              break;
          }
          return temp;
        },
      },
      {
        field: "name",
        title: "Name",
        sorter: "string",
      },
    ];

    if (tags) {
      tags.forEach((item) => {
        _c.push({
          title: item.name,
          field: item.id,
          sorter: (a, b, aRow, bRow) => {
            let _a: any = aRow.getData();
            let _b: any = bRow.getData();
            _a = renderTag(item.id, _a);
            _b = renderTag(item.id, _b);
            if (_a && _b) {
              return _a.localeCompare(_b);
            }
            return 0;
          },
          formatter: tagFormatter as any,
        });
      });
    }
    return _c;
  }, [renderTag, tagFormatter, tags]);

  const [showModal, setShowModal] = useState(false);

  const style = useMemo(
    () => ({
      position: "absolute" as "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      maxHeight: "600px",
      overflow: "hidden",
      width: 400,
      bgcolor: "background.paper",
      border: "1px solid #000",
      borderRadius: "4px",
      boxShadow: 5,
      display: "flex",
      flexDirection: "column",
    }),
    [],
  );

  const editorRef = useRef<any>(null);

  const updateTag = useCallback(
    async (strategy) => {
      const strategyAPI = new Strategies(auth.user35);
      const combinedStrategiesAPI = new CombinedStrategies();
      const combinedProductsAPI = new CombinedProducts();

      const onError = (error) => {
        const errorStatement = `Error on updating tags! (${SHARED_OBJECT_TYPE_SERVER_TO_UI[strategy.type]} - ${strategy.id})`;
        console.log(errorStatement, error);
        feedback.show({
          type: "error",
          message: errorStatement,
        });
      };
      const onSuccess = (responseUpdate) => {
        //! as response is an obj representing the strategy to check if it's all ok i'll
        //! make the check by the strategy id
        if (responseUpdate.id != null) {
          const successStatement = `updated tags of "${responseUpdate.name}"(${SHARED_OBJECT_TYPE_SERVER_TO_UI[strategy.type]}) successfully!`;
          feedback.show({
            type: "success",
            message: successStatement,
          });
          // update the row in the table directly
          if (tabulatorRef.current) {
            const table = tabulatorRef.current.getTableInstance();
            //updateData: This function takes an array of
            //row objects and will update each row based on its index value.
            table.updateData([responseUpdate]);
            let data = table.getData();
            //replaceData: function lets you silently replace all data
            //in the table without updating scroll position, sort or filtering
            table.replaceData(data);
            let row = table.getRow(responseUpdate.id);
            row.deselect();
          }
        } else {
          throw new Error("there was an error on updating tags!");
        }
        return responseUpdate;
      };
      //get the hole strategy here
      let wholeObject: any = null;
      switch (strategy.type) {
        case SHARED_OBJECT_TYPE_UI_TO_SERVER.combinedProduct:
          wholeObject = await combinedProductsAPI.get(
            strategy.id,
            SHARED_OBJECT_TYPE_UI_TO_SERVER.combinedProduct,
            auth.user35.id,
          );
          wholeObject.tags = strategy.tags;
          return combinedProductsAPI
            .update(wholeObject, null, auth.user35.id)
            .then(onSuccess, onError);
        case SHARED_OBJECT_TYPE_UI_TO_SERVER.combinedStrategy:
          wholeObject = await combinedStrategiesAPI.get(
            strategy.id,
            SHARED_OBJECT_TYPE_UI_TO_SERVER.combinedStrategy,
            auth.user35.id,
          );
          wholeObject.tags = strategy.tags;
          return combinedStrategiesAPI
            .update(wholeObject, null, auth.user35.id)
            .then(onSuccess, onError);
        case SHARED_OBJECT_TYPE_UI_TO_SERVER.strategy:
          wholeObject = await strategyAPI.get(strategy.id);
          wholeObject.tags = strategy.tags;
          return strategyAPI.update(wholeObject).then(onSuccess, onError);
        case SHARED_OBJECT_TYPE_UI_TO_SERVER.screening:
          wholeObject = await combinedStrategiesAPI.get(
            strategy.id,
            SHARED_OBJECT_TYPE_UI_TO_SERVER.screening,
            auth.user35.id,
          );
          // perchè utilizzo la combinedStrategiesAPI?
          // perché lgi screening sono degli stored object di tipo screening,
          // e combinedStrategiesAPI estende la classe StoredObjectType.
          // perciò posso utilizzare combinedStrategiesAPI per fare get e update degli screening, specificando il tipo screening.
          wholeObject.tags = strategy.tags;
          return combinedStrategiesAPI
            .update(
              wholeObject,
              SHARED_OBJECT_TYPE_UI_TO_SERVER.screening,
              auth.user35.id,
            )
            .then(onSuccess, onError);
        default:
          debugger;
      }
    },
    [auth.user35, feedback],
  );

  const applyHandler = useCallback(() => {
    if (editorRef.current != null) {
      let edited = editorRef?.current?.getObjFilters();
      let strategy = deepClone(selectedStrategy);
      strategy.tags = {
        type: "object",
        data: edited,
      };
      updateTag(strategy);
    }
    setShowModal(false);
  }, [selectedStrategy, updateTag]);

  const tabulatorRef = useRef<any>(null);
  const onRowClickHandler = useCallback((e, row) => {
    let data = row.getData();
    if (data.id != null) {
      setShowModal(true);
      setSelectedStrategy(data);
    }
  }, []);

  const [tableReady, setTableReady] = useState(false);
  const [columnsReady, setColumnsReady] = useState(false);

  const updateTableData = useCallback(async () => {
    if (tableReady && columnsReady) {
      console.log("Updating table data...");
      if (tabulatorRef.current && objects.length > 0) {
        const table = tabulatorRef.current.getTableInstance();
        let data = table.getData();
        if (table.initialized) {
          if (data.length <= 0) {
            await table.setData(objects).then(() => {
              console.log("Table data set with new objects.", objects);
              table.redraw();
            });
          } else {
            await table.replaceData(objects);
            console.log("Table data replaced with new objects.", objects);
          }
        }
      }
    }
  }, [columnsReady, objects, tableReady]);

  const events: EventsType = useMemo(
    () => [
      {
        event: "tableBuilt",
        callback: () => {
          setTableReady(true);
        },
      },
      {
        event: "tableDestroyed",
        callback: () => setTableReady(false),
      },
      { event: "rowClick", callback: onRowClickHandler },
      {
        event: "columnsLoaded",
        callback: (e, row) => {
          setColumnsReady(true);
        },
      },
    ],
    [onRowClickHandler],
  );

  const tableOptions = useMemo(
    () => ({
      layout: "fitDataStretch",
      selectableRows: 1,
    }),
    [],
  );

  const tableComponent = useMemo(
    () => (
      <Tabulator options={tableOptions} ref={tabulatorRef} events={events} />
    ),
    [events, tableOptions],
  );

  const modal = useMemo(() => {
    return showModal ? (
      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <Box sx={style}>
          <Box p={2}>
            <Typography>
              <strong>
                Edit Tags for{" "}
                {SHARED_OBJECT_TYPE_SERVER_TO_UI[selectedStrategy.type]}:{" "}
                {selectedStrategy.name}
              </strong>
            </Typography>
          </Box>
          <Editor
            ref={editorRef}
            selectedStrategy={selectedStrategy}
            tags={tags}
          />
          <Box p={2} display={"flex"} justifyContent={"flex-end"} gap={2}>
            <Button onClick={applyHandler} color="secondary">
              apply
            </Button>
            <Button
              onClick={() => {
                setShowModal(false);
                const table = tabulatorRef.current.getTableInstance();
                let row = table.getRow(selectedStrategy.id);
                row.deselect();
              }}
            >
              cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    ) : null;
  }, [applyHandler, selectedStrategy, showModal, style, tags]);

  const onChangeTabHandler = useCallback((value: TagManagerTabsType) => {
    const table = tabulatorRef.current.getTableInstance();
    table.clearData();
    setTab(value);
  }, []);

  useEffect(() => {
    if (tableReady) {
      if (tabulatorRef.current && _columns) {
        const table = tabulatorRef.current.getTableInstance();
        table.initialized && table.setColumns(_columns);
      }
    }
  }, [_columns, tableReady]);

  useEffect(() => {
    updateTableData();
  }, [updateTableData]);

  useEffect(() => {
    getTags().then((tags) => {
      switch (tab) {
        case "strategy":
          const strategyTags = tags.filter((item) => item.id !== "group-menu");
          setTags(strategyTags);

          break;
        case "screening":
          const screeningTags = tags.filter((item) => item.id === "group-menu");
          setTags(screeningTags);
          break;
        default:
          alert("error, tab not recognized");
      }
    });
  }, [getTags, tab]);

  useEffect(() => {
    console.log("Selected tab changed to:", tab);
    switch (tab) {
      case "strategy":
        fetchStrategies();
        break;
      case "screening":
        fetchScreenings();
        break;
      default:
        alert("error, tab not recognized");
    }
    // fetchStrategies();
  }, [fetchScreenings, fetchStrategies, tab]);

  return (
    <Box
      height={"100%"}
      width={"100%"}
      gap={2}
      borderRadius={1}
      display={"flex"}
      overflow={"auto"}
    >
      <Box display={"flex"} p={0.5} gap={2} width={"100%"} overflow={"hidden"}>
        <Box
          display={"flex"}
          gap={1}
          boxShadow={3}
          justifyContent={"flex-start"}
          alignContent={"flex-start"}
          p={1}
          overflow={"auto"}
          borderRadius={2}
          flexDirection={"column"}
        >
          {itemButtons.map((button) => (
            <Button
              key={button.value}
              title={button.title}
              color={tab === button.value ? "secondary" : "primary"}
              onClick={() => onChangeTabHandler(button.value)}
            >
              {button.title}
            </Button>
          ))}
        </Box>
        <Box>
          {isLoading && (
            <Box
              bgcolor={"white"}
              display={"flex"}
              gap={2}
              zIndex={3}
              position={"absolute"}
              top={0}
              left={0}
              width={"100%"}
              height={"100%"}
              flexDirection={"column"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Typography>Loading data...</Typography>
              <CircularProgress color="secondary" />
            </Box>
          )}

          <Box
            sx={{ boxShadow: 3 }}
            borderRadius={"4px"}
            overflow={"hidden"}
            display={"flex"}
            height={"100%"}
            width={"100%"}
          >
            {modal}
            {tableComponent}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export const fromArrToObj = (arrFilters: string[], tags: any) => {
  const objToReturn: any = {};
  tags.forEach((element) => {
    objToReturn[element.id] = null;
    const actualFilters: string[] = element.filters.map((item) => item.id);
    arrFilters.forEach((strfilter: string) => {
      if (actualFilters.includes(strfilter)) {
        switch (element.type) {
          case "single":
            objToReturn[element.id] = strfilter;
            break;
          case "multiple":
            if (Array.isArray(objToReturn[element.id])) {
              objToReturn[element.id] = [...objToReturn[element.id], strfilter];
            } else {
              objToReturn[element.id] = [strfilter];
            }
            break;
        }
      }
    });
  });
  return objToReturn;
};
